import { fetchData } from 'Utils/fetch-data';
import type {
  EmployeesByCourseReportI,
  CourseI,
  CourseEmployeesReportTotalsI,
  CourseEmployeesReportFilterI,
  CourseEmployeeForApprovalI,
  CourseEmployeesForApprovalQuery,
} from 'types';
import { BaseApi } from './base-api';

class CoursesApi extends BaseApi<CourseI, any> {
  /**
   * Obtiene todos los empleados que pertenecen a un curso en específico.
   * @param courseId - ID del curso
   * @param filters - Filtros para la consulta
   * @returns Lista de empleados que pertenecen a ese curso.
   */
  async readCourseEmployees(courseId: string, filters: CourseEmployeesReportFilterI) {
    const response = await fetchData<EmployeesByCourseReportI[]>(`${this.endpoint}/${courseId}/employees`, {
      method: 'GET',
      params: filters,
    });
    return response;
  }

  /**
   * Lista paginada de empleados activos enrolados en un curso, con filtros server-side.
   * Diseñado para el flujo de aprobación masiva. Devuelve `userCourse.id` como `id` (no `User.id`).
   *
   * Endpoint: GET /api/courses/:id/employees con shape paginado { data, pagination }.
   */
  async readCourseEmployeesForApproval(
    courseId: string,
    params: CourseEmployeesForApprovalQuery = {}
  ) {
    const response = await fetchData<CourseEmployeeForApprovalI[]>(
      `${this.endpoint}/${courseId}/employees`,
      {
        method: 'GET',
        params,
        includePagination: true,
      }
    );
    return response;
  }

  /**
   * Obtiene los datos totales de un curso en específico.
   * @param courseId - ID del curso
   */
  async readCourseEmployeesTotals(courseId: string) {
    const response = await fetchData<CourseEmployeesReportTotalsI>(`${this.endpoint}/${courseId}/totals`, {
      method: 'GET',
    });
    return response;
  }

  /**
   * Descarga el certificado de un curso de un empleado
   * @param courseId - ID del curso
   * @param employeeId - ID del empleado
   * @param certificationId - ID de la certificación
   * @returns Blob del certificado
   */
  async downloadCertificate(courseId: string, employeeId: string, certificationId: string) {
    const response = await fetchData<Blob>(
      `${this.endpoint}/${courseId}/employee/${employeeId}/certification/${certificationId}/download`,
      {
        method: 'GET',
        responseType: 'blob',
      }
    );
    return response;
  }

  /**
   * Actualiza las calificaciones de los empleados de una lección manual.
   * @param courseId - ID del curso
   * @param lessonId - ID de la lección
   * @param editValues - Valores de los empleados a actualizar
   * @returns Respuesta de la API
   */
  async updateGrades(courseId: string, lessonId: string, editValues: any) {
    const response = await fetchData(`${this.endpoint}/${courseId}/lessons/${lessonId}/update-grades`, {
      method: 'PUT',
      data: editValues,
    });
    return response;
  }
}

const coursesApi = new CoursesApi('/courses');

export default coursesApi;
